import apiConfig from "app/api/apiConfig.json";
import { getLocalApi } from "./localApi";

export type ReportTextReferenceDataApiItem = {
  id: string;
  name: string;
  usedIn: string;
};

export type ReportTextReferenceDataItem = ReportTextReferenceDataApiItem & {
  reportTextId: number;
  supportsForWhom?: boolean;
};

export type ReportTextScope = "Division" | "Centre";

const parseReportTextId = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (!/^\d+$/.test(trimmed)) {
      return null;
    }
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const parseReportTextIdFromItem = (item: any): number | null => {
  const fromDedicated =
    parseReportTextId(item?.reportTextId) ?? parseReportTextId(item?.ReportTextId);
  if (fromDedicated !== null) {
    return fromDedicated;
  }
  return parseReportTextId(item?.id) ?? parseReportTextId(item?.Id);
};

const unwrapReportTextList = (data: unknown): any[] => {
  if (Array.isArray(data)) {
    return data;
  }
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    const nested = o.data ?? o.Data ?? o.value ?? o.Value ?? o.items ?? o.Items;
    if (Array.isArray(nested)) {
      return nested;
    }
  }
  return [];
};

export async function getReportTextReferenceData(
  scope?: ReportTextScope
): Promise<ReportTextReferenceDataItem[]> {
  const api = await getLocalApi();
  const base = String((apiConfig as any).reference_data_get_report_text ?? "").trim();
  const safeBase = base || "/api/ReferenceData/report-text";
  const path =
    scope
      ? `${safeBase}${safeBase.includes("?") ? "&" : "?"}scope=${encodeURIComponent(scope)}`
      : safeBase;
  const response = await api.fetchList(
    path,
    (dto: any) => dto as ReportTextReferenceDataApiItem,
    "Unable to fetch report text reference data",
    unwrapReportTextList
  );

  if (!Array.isArray(response)) {
    return [];
  }

  return response
    .map((item: any) => {
      const reportTextId = parseReportTextIdFromItem(item);
      if (reportTextId === null) {
        return null;
      }

      const referenceKey =
        item?.reportTextId ??
        item?.ReportTextId ??
        item?.id ??
        item?.Id ??
        reportTextId;

      return {
        id: String(referenceKey),
        name: String(item?.name ?? item?.Name ?? ""),
        usedIn: String(item?.usedIn ?? item?.UsedIn ?? ""),
        reportTextId,
        supportsForWhom: Boolean(item?.supportsForWhom ?? item?.SupportsForWhom),
      } as ReportTextReferenceDataItem;
    })
    .filter((item): item is ReportTextReferenceDataItem => item !== null);
}
